import MobileModalFrame from "../common-components/mobile-modal-frame";
import { useTranslation as useInterfaceTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { createOrderQuote } from "Services/supplier-pricing";
import { PricingResult } from "../../utils/supplier-pricing-engine";
import { PBtn, fmt, fmtDate } from "./ui";

type Props = {
  show: boolean;
  onHide: () => void;
  orderId: number;
  result: PricingResult;
  customerId: number | null;
  business?: any;
  paymentTerms?: string;
  discountPercent?: number;
  validityDays: number;
};

type Snapshot = {
  quote_no: string;
  issued_at: string;
  valid_until: string;
  order: { order_no: string; name: string | null };
  supplier: { name: string };
  customer: { name: string | null; site: string | null; address: string | null; payment_terms: string | null; discount_percent: string };
  items: Array<{
    id: number; name: string; diameter: string | null; quantity: number;
    weight_ton: string; material_per_ton: string | null; charge_type: string; total: string;
  }>;
  totals: Record<string, string | number>;
};

export default function QuoteModal({ show, onHide, orderId }: Props) {
  const { t: uiT } = useInterfaceTranslation("interface");
  const { t } = useTranslation(["supplierPricing"]);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  useEffect(() => {
    if (!show) return;
    setSnapshot(null);
    createOrderQuote(orderId)
      .then((response: any) => setSnapshot(response.data.snapshot))
      .catch(() => {
        toast.error(t("common.error"));
        onHide();
      });
  }, [show, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const print = () => {
    document.body.classList.add("eb-pr-printing");
    window.print();
    document.body.classList.remove("eb-pr-printing");
  };
  const amount = (key: string) => Number(snapshot?.totals[key] ?? 0);
  return (
    <Modal show={show} onHide={onHide} centered size="xl" dialogClassName="eb-pr eb-pr-modal">
<MobileModalFrame onClose={onHide}>
      <div className="eb-pr-modal-body">
        {!snapshot ? <div style={{ textAlign: "center", padding: 60 }}><Spinner animation="border" /></div> : (
          <>
            <div style={{ overflow: "auto", maxHeight: "70vh", display: "flex", justifyContent: "center", background: "#f3f4f6", padding: 16 }}>
              <div className="eb-pr-a4">
                <div className="eb-pr-between">
                  <div><h2 className="eb-pr-modal-title">{snapshot.supplier.name}</h2></div>
                  <div style={{ textAlign: "left" }}>
                    <h2 className="eb-pr-modal-title">{t("quote.title", { no: snapshot.quote_no })}</h2>
                    <div className="eb-pr-muted">{t("quote.date")}: {fmtDate(snapshot.issued_at)} · {t("quote.validUntil")}: {fmtDate(snapshot.valid_until)}</div>
                    <div className="eb-pr-muted">{t("quote.order")}: {snapshot.order.order_no}</div>
                  </div>
                </div>
                <div className="eb-pr-card" style={{ padding: 14 }}>
                  <div><b>{t("quote.to")}:</b> {snapshot.customer.name || "—"}</div>
                  {snapshot.customer.site && <div><b>{t("quote.site")}:</b> {snapshot.customer.site} {snapshot.customer.address}</div>}
                  {snapshot.customer.payment_terms && <div><b>{t("quote.terms")}:</b> {t(`customerTab.terms.${snapshot.customer.payment_terms}`)}</div>}
                </div>
                <table>
                  <thead><tr><th>#</th><th>{t("quote.description")}</th><th>{t("quote.qty")}</th><th>{t("quote.weight")}</th><th>{t("quote.perTon")}</th><th>{t("quote.sum")}</th></tr></thead>
                  <tbody>{snapshot.items.map((row, index) => (
                    <tr key={row.id}>
                      <td>{index + 1}</td>
                      <td>{row.name} · Φ{row.diameter || "—"} · {t(`charge.${row.charge_type}`)}</td>
                      <td>{row.quantity}</td>
                      <td>{fmt(Number(row.weight_ton) * 1000, 1)}</td>
                      <td>{row.material_per_ton !== null ? fmt(Number(row.material_per_ton), 0) : "—"}</td>
                      <td>{fmt(Number(row.total))}</td>
                    </tr>
                  ))}</tbody>
                </table>
                <div className="totals">
                  <div><span>{t("order.material", { tons: amount("weight_ton").toFixed(3) })}</span><span>{fmt(amount("material"))}</span></div>
                  <div><span>{t("analysis.processing")}</span><span>{fmt(amount("bend") + amount("cut") + amount("units"))}</span></div>
                  <div><span>{t("order.transport")}</span><span>{fmt(amount("transport"))}</span></div>
                  {amount("discount") !== 0 && <div><span>{t("order.discount", { pct: snapshot.customer.discount_percent })}</span><span>{fmt(amount("discount"))}</span></div>}
                  <div><span>{t("order.subtotal")}</span><span>{fmt(amount("subtotal"))}</span></div>
                  <div><span>{t("order.vat", { pct: amount("vat_percent") })}</span><span>{fmt(amount("vat"))}</span></div>
                  <div className="strong"><span>{t("order.total")}</span><span>{fmt(amount("total"))} {uiT("NIS")} </span></div>
                </div>
                <div style={{ marginTop: "auto", borderTop: "1px solid #ccc", paddingTop: 8, width: 240 }}>{t("quote.customerSign")}</div>
              </div>
            </div>
            <div className="eb-pr-footer">
              <PBtn kind="secondary" onClick={onHide}>{t("common.close")}</PBtn>
              <PBtn kind="ghost" onClick={print}>{t("analysis.print")}</PBtn>
            </div>
          </>
        )}
      </div>
    </MobileModalFrame>
</Modal>
  );
}
